export type TenantSessionEvent =
    | { type: 'switch-started' }
    | { type: 'switch-completed' }
    | { type: 'switch-cancelled' };

const CHANNEL_NAME = 'atomix-tenant-session';
const STORAGE_KEY = 'atomix-tenant-session-event';
const tabId = typeof crypto === 'undefined' ? 'server' : crypto.randomUUID();

let observedStytchOrganizationId: string | null = null;

export const getObservedStytchOrganizationId = () =>
    observedStytchOrganizationId;

export const setObservedStytchOrganizationId = (
    organizationId: string | null,
) => {
    observedStytchOrganizationId = organizationId;
};

export const publishTenantSessionEvent = (event: TenantSessionEvent) => {
    if (typeof window === 'undefined') return;
    const message = { ...event, sender: tabId, nonce: crypto.randomUUID() };
    try {
        if ('BroadcastChannel' in window) {
            const channel = new BroadcastChannel(CHANNEL_NAME);
            channel.postMessage(message);
            channel.close();
        }
    } catch {
        // The focus-time server check remains available when messaging is blocked.
    }
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(message));
    } catch {
        // Storage may be unavailable in hardened browser contexts.
    }
};

export const subscribeToTenantSessionEvents = (
    listener: (event: TenantSessionEvent) => void,
) => {
    if (typeof window === 'undefined') return () => undefined;
    const handleMessage = (
        message: TenantSessionEvent & { sender?: string },
    ) => {
        if (message.sender !== tabId) listener(message);
    };
    const channel =
        'BroadcastChannel' in window
            ? new BroadcastChannel(CHANNEL_NAME)
            : null;
    channel?.addEventListener('message', (event) => handleMessage(event.data));

    const handleStorage = (event: StorageEvent) => {
        if (event.key !== STORAGE_KEY || !event.newValue) return;
        try {
            handleMessage(JSON.parse(event.newValue));
        } catch {
            // Ignore malformed local storage written outside this application.
        }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
        channel?.close();
        window.removeEventListener('storage', handleStorage);
    };
};
